import React, { useEffect } from 'react';
import {
  AlertTriangle, CheckCircle2, ChevronLeft, ChevronRight,
  Inbox, RefreshCw, WifiOff, X, ArrowLeft,
} from 'lucide-react';

// ─── Brand constants ─────────────────────────────────────────────────────────
export const URITHI = {
  navy: '#001f3f',
  navyMid: '#0a3260',
  gold: '#C9982A',
  green: '#059669',
};

// ─── Toast ───────────────────────────────────────────────────────────────────
export const Toast = ({ open, message, severity = 'success', onClose }) => {
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => onClose && onClose(), 3500);
    return () => clearTimeout(t);
  }, [open, onClose]);

  if (!open) return null;

  const config = {
    success: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-800', icon: <CheckCircle2 className="w-5 h-5 text-emerald-600" /> },
    error:   { bg: 'bg-red-50',     border: 'border-red-200',     text: 'text-red-800',     icon: <AlertTriangle className="w-5 h-5 text-red-600" /> },
    info:    { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-800',    icon: <CheckCircle2 className="w-5 h-5 text-blue-600" /> },
  }[severity] || {};

  return (
    <div className="fixed top-6 right-6 z-50">
      <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${config.bg} ${config.border} shadow-sm min-w-[280px]`}>
        {config.icon}
        <span className={`text-sm font-medium flex-1 ${config.text}`}>{message}</span>
        <button onClick={onClose} className={`${config.text} hover:opacity-70 transition-opacity`}>
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export const LoadingState = ({ message = 'Loading…' }) => (
  <div className="flex items-center justify-center py-16">
    <div className="text-center">
      <div className="inline-block w-12 h-12 border-4 border-[#001f3f] border-t-transparent rounded-full animate-spin mb-4" />
      <p className="text-sm text-gray-500 font-medium">{message}</p>
    </div>
  </div>
);

export const ErrorState = ({ title = 'Failed to load', message = 'Please check your connection and try again.', onRetry }) => (
  <div className="flex items-center justify-center py-16">
    <div className="text-center max-w-sm">
      <div className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-red-50 mb-4">
        <WifiOff className="w-7 h-7 text-red-500" />
      </div>
      <h3 className="text-base font-bold text-[#001f3f] mb-1">{title}</h3>
      <p className="text-sm text-gray-500 mb-4">{message}</p>
      {onRetry && (
        <button onClick={onRetry} className="inline-flex items-center gap-2 bg-[#001f3f] text-white px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#0a3260] transition-colors">
          <RefreshCw className="w-4 h-4" />
          Try Again
        </button>
      )}
    </div>
  </div>
);

export const EmptyState = ({ title = 'No data yet', message = 'Records will appear here once added.', action }) => (
  <div className="flex items-center justify-center py-16">
    <div className="text-center max-w-sm">
      <div className="inline-flex w-14 h-14 items-center justify-center rounded-full bg-gray-100 mb-4">
        <Inbox className="w-7 h-7 text-gray-400" />
      </div>
      <h3 className="text-base font-bold text-[#001f3f] mb-1">{title}</h3>
      <p className="text-sm text-gray-500 mb-4">{message}</p>
      {action}
    </div>
  </div>
);

export const Pagination = ({ page, rowsPerPage, total, onPageChange, onRowsPerPageChange }) => {
  const totalPages = Math.max(1, Math.ceil(total / rowsPerPage));
  const start = total === 0 ? 0 : page * rowsPerPage + 1;
  const end = Math.min((page + 1) * rowsPerPage, total);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-5 py-4 border-t border-gray-100 bg-gray-50">
      <div className="flex items-center gap-3">
        <span className="text-xs text-gray-600 font-medium">Rows per page:</span>
        <select
          value={rowsPerPage}
          onChange={(e) => onRowsPerPageChange(parseInt(e.target.value, 10))}
          className="text-xs border border-gray-200 rounded-lg bg-white px-2 py-1 focus:outline-none focus:border-[#001f3f] font-medium"
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={25}>25</option>
        </select>
      </div>
      <span className="text-xs text-gray-600 font-medium">{start}–{end} of {total}</span>
      <div className="flex items-center gap-1">
        <button onClick={() => onPageChange(page - 1)} disabled={page === 0} className="p-2 rounded-lg text-gray-500 hover:bg-white hover:text-[#001f3f] disabled:opacity-40 transition-colors">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button onClick={() => onPageChange(page + 1)} disabled={page >= totalPages - 1} className="p-2 rounded-lg text-gray-500 hover:bg-white hover:text-[#001f3f] disabled:opacity-40 transition-colors">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export const ConfirmModal = ({ open, title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel', onConfirm, onCancel, loading = false, danger = false }) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden">
        <div className="p-6">
          <div className={`inline-flex w-12 h-12 items-center justify-center rounded-full mb-4 ${danger ? 'bg-red-50' : 'bg-blue-50'}`}>
            <AlertTriangle className={`w-6 h-6 ${danger ? 'text-red-600' : 'text-[#001f3f]'}`} />
          </div>
          <h3 className="text-lg font-bold text-[#001f3f] mb-2">{title}</h3>
          <p className="text-sm text-gray-600 leading-relaxed">{message}</p>
        </div>
        <div className="flex gap-2 p-4 bg-gray-50 border-t border-gray-100">
          <button onClick={onCancel} className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 bg-white text-gray-700 text-sm font-semibold hover:bg-gray-50 transition-colors">
            {cancelLabel}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`flex-1 px-4 py-2.5 rounded-lg text-white text-sm font-semibold transition-colors disabled:opacity-50 ${danger ? 'bg-red-600 hover:bg-red-700' : 'bg-[#001f3f] hover:bg-[#0a3260]'}`}
          >
            {loading ? 'Processing…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

export const BackButton = ({ onClick, label = 'Back' }) => (
  <button onClick={onClick} className="inline-flex items-center gap-2 text-[#001f3f] hover:text-[#0a3260] mb-4 text-sm font-semibold transition-colors group">
    <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
    {label}
  </button>
);

export const StatusBadge = ({ status, variant }) => {
  const variants = {
    success: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    pending: 'bg-amber-50 text-amber-700 border-amber-200',
    danger:  'bg-red-50 text-red-700 border-red-200',
    info:    'bg-blue-50 text-blue-700 border-blue-200',
    neutral: 'bg-gray-50 text-gray-700 border-gray-200',
  };
  const auto = (() => {
    const s = (status || '').toString().toLowerCase();
    if (['completed', 'paid', 'active', 'success', 'new'].includes(s)) return 'success';
    if (['pending', 'intransit', 'in transit', 'processing', 'used'].includes(s)) return 'pending';
    if (['failed', 'cancelled', 'rejected'].includes(s)) return 'danger';
    if (['refurb', 'refurbished'].includes(s)) return 'info';
    return 'neutral';
  })();
  const v = variants[variant || auto];
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide border ${v}`}>{status}</span>;
};

// ─── Image gallery thumbnails (used in table rows) ──────────────────────────
export const ImageStack = ({ images, primary, alt }) => {
  const list = (images || []).slice(0, 3);
  const extra = (images || []).length - 3;

  if (list.length === 0 && primary) {
    return <img src={primary} alt={alt} className="w-10 h-10 rounded-xl object-cover border border-gray-200 bg-gray-50" />;
  }
  if (list.length === 0) {
    return <div className="w-10 h-10 rounded-xl bg-gray-100 border border-gray-200" />;
  }
  return (
    <div className="flex -space-x-2">
      {list.map((img, i) => (
        <img
          key={img.id || i}
          src={img.image_url || img.image}
          alt={`${alt}-${i}`}
          className="w-10 h-10 rounded-xl object-cover border-2 border-white bg-gray-50"
        />
      ))}
      {extra > 0 && (
        <div className="w-10 h-10 rounded-xl bg-[#001f3f] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
          +{extra}
        </div>
      )}
    </div>
  );
};